import {
  createInitialDataLayer,
  PageType,
} from '@redteclab/gtm-datalayer-library'

import { type PrivateGlobalConfig } from '../../global-config'
import {
  GTM_CONST_APPLICATION_NAME,
  type GtmEventData,
  gtmMakeBaseGtmData,
} from '../../gtm'

const getSearchPhrase = (searchParams: URLSearchParams): string =>
  searchParams.get('query') ?? searchParams.get('q') ?? ''

/**
 * If there's neither of these params, but we got a search results page,
 * then it's defined as an "external search" which might be triggered
 * for users coming from Newsletter, Google, etc. and landing on a SERP.
 * But also onsite this might happen if you click on "show all" in the cross-sell widgets.
 *
 * Every search that has happened onsite in the shop (triggered via the search box),
 * gets a `i=1` param as is defined as "internal search" in econda tracking.
 *
 * `B=1` is triggered by a click on a banner which redirects to a SERP (`b` is an alias).
 */
export const getSearchType = (searchParams: URLSearchParams): string => {
  if (searchParams.get('i') === '1') {
    return 'interne'
  }

  if (searchParams.get('B') === '1' || searchParams.get('b') === '1') {
    return 'teaser'
  }

  return 'externe'
}

type SearchPageGtmEventData = GtmEventData & {
  search: {
    searchPhrase: string
    searchType: string
  }
}

export const searchPageGtmBuildData = ({
  privateGlobalConfig,
  uri,
}: {
  privateGlobalConfig: PrivateGlobalConfig
  uri: string
}): { gtmDataToPush: [SearchPageGtmEventData] } => {
  const url = new URL(uri, privateGlobalConfig.shopUrl)

  const dataLayerData = gtmMakeBaseGtmData({ privateGlobalConfig })
  const initialDataLayer = createInitialDataLayer({
    application: GTM_CONST_APPLICATION_NAME,
    dataLayerData,
    pageType: PageType.SEARCH_RESULTS,
    url,
  })

  const gtmData = {
    ...initialDataLayer,
    search: {
      searchPhrase: getSearchPhrase(url.searchParams),
      searchType: getSearchType(url.searchParams),
    },
  }

  return {
    gtmDataToPush: [gtmData],
  }
}
